// ============================================================
// StaffIntake.jsx — 직원 모바일 물건접수폼
//
// [목적]
//  PC 에디터(EstateEditor)는 항목이 200개가 넘어 현장에서 쓰기 어렵다.
//  원룸·투룸 월세를 한 손으로 1~2분 안에 등록하는 것이 목표.
//
// [전체 흐름]
//   [등록 시작] 버튼
//     → 마이크 권한 요청 + 녹음 시작
//     → 기본지역(관리탭 설정) 읽어오기
//   주소 선택 → 백그라운드로 건물정보 조회 시작 (직원은 기다리지 않음)
//   금액·방수·사진·연락처 입력
//   [저장]
//     → 건물정보 도착 대기(대개 이미 완료)
//     → POST /api/properties/new       매물번호 발급
//     → PUT  /:no/basic                이름·호수·층·종류
//     → PUT  /:no/detail               금액·면적·방수·준공일
//     → PUT  /:no/meta                 연락처
//     → 사진 업로드
//     → 녹음 업로드 (실패해도 매물은 이미 저장됨)
//
// [연관 파일]
//   components/NumPad.jsx            커스텀 키패드
//   pages/StaffIntake.css            스타일
//   api/authFetch.js                 토큰 붙은 fetch
//   api/photoApi.js                  사진 업로드
//   backend/routes/staffIntake.js    기본지역·녹음 API
// ============================================================
import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import NumPad from "../components/NumPad";
import { authFetch } from "../api/authFetch";
import { matchRoad } from "../utils/chosung";   // 도로명 초성 검색
import "./StaffIntake.css";

// ── 선택지 정의 ────────────────────────────────────────
// 값(value)은 반드시 기존 매물과 같아야 검색 필터에 잡힌다.
// EstateEditor.jsx 의 ESTATE_TYPE_OPTIONS / BLD_TYPE_OPTIONS 에서 가져온 코드값.
const ESTATE_TYPES = [
  { value: "residence", label: "주거용" },
  { value: "commerce",  label: "상업용" },
  { value: "business",  label: "업무용" },
  { value: "profit",    label: "수익성" },
];

// 자주 쓰는 용도만 버튼으로. 나머지는 '기타' 선택 시 드롭다운.
const BLD_QUICK = [
  { value: "single-room",     label: "원룸" },
  { value: "multi-household", label: "다가구" },
  { value: "villa",           label: "빌라" },
  { value: "apt",             label: "아파트" },
  { value: "e-apt",           label: "오피스텔" },
  { value: "shop",            label: "상가" },
  { value: "office",          label: "사무실" },
];

// '기타' 선택 시 펼쳐지는 전체 목록 (constants/bldLabels.js 와 동일)
const BLD_ALL = [
  { value: "business-bld",      label: "업무용빌딩" },
  { value: "shop-house",        label: "상가주택" },
  { value: "land",              label: "토지" },
  { value: "sale-shop",         label: "분양상가" },
  { value: "s-home-bld",        label: "단독주택" },
  { value: "multi-purpose",     label: "다용도" },
  { value: "metro-house",       label: "도시형주택" },
  { value: "row-house",         label: "연립주택" },
  { value: "single-room-owner", label: "원룸주인세대" },
  { value: "multi-house",       label: "다세대" },
  { value: "gosiwon",           label: "고시원건물" },
  { value: "farm-house",        label: "농가주택" },
  { value: "house-site",        label: "택지" },
  { value: "etc",               label: "기타" },
];

// 거래유형 — 중복 선택 가능. DB 컬럼은 각각 'y'/'n' 문자.
const DEAL_TYPES = [
  { key: "rent_type_2month", label: "월세" },
  { key: "rent_type_1year",  label: "전세" },
  { key: "rent_type_4deal",  label: "매매" },   // [INTAKE_FIX_V1] 매매=4deal (5sale 은 분양 — 00 §5-17)
  { key: "rent_type_3short", label: "단기" },
];

// 룸형태 — DB 컬럼 bld_structure
//   기존 매물 분포: open 4567 / separate 4139 / multi 100
//   ※ PC 에디터에는 현재 이 입력칸이 빠져 있다(배열만 남고 화면에서 제거됨).
//     검색 필터는 이 값을 그대로 쓰므로 코드값을 반드시 맞춰야 한다.
const ROOM_STRUCTURES = [
  { value: "open",     label: "오픈형" },
  { value: "separate", label: "분리형" },
  { value: "multi",    label: "복층형" },
];

// 층대체 — 호수가 없는 매물에 쓴다 (EstateEditor 의 FLOOR_TYPE2_OPTIONS 와 동일)
const FLOOR_ALT = [
  { value: "top",     label: "옥탑" },
  { value: "bottom",  label: "바닥층" },
  { value: "parking", label: "주차층" },
];

// 연락처 관계 — owners(임대인) / tenants(임차인) 각각 다른 선택지를 쓴다
const OWNER_RELATIONS  = ["집주인", "관리인", "가족", "대리인"];
const TENANT_RELATIONS = ["세입자", "관리인", "기타"];

// 룸·욕실 개수를 물어볼 물건유형
//   상업용·업무용은 방 개수를 세지 않으므로 화면에서 숨긴다
const NEEDS_ROOM = ["residence", "profit"];

// 관리비 포함 항목 — 토글. DB 컬럼은 'y'/'n'.
const MGR_ITEMS = [
  { key: "price_mgr_item_basic",    label: "기본" },
  { key: "price_mgr_item_water",    label: "수도" },
  { key: "price_mgr_item_elec",     label: "전기" },
  { key: "price_mgr_item_heat",     label: "난방" },
  { key: "price_mgr_item_internet", label: "인터넷" },
  { key: "price_mgr_item_online",   label: "TV" },
];

// 임시저장 키 — 전화가 와서 앱이 꺼져도 값이 남도록 브라우저에 저장
const DRAFT_KEY = "staff_intake_draft_v1";

export default function StaffIntake() {
  const navigate = useNavigate();

  // ── 단계 관리 ────────────────────────────────────────
  // started=false 면 시작 화면, true 면 입력 화면
  const [started, setStarted] = useState(false);
  const [saving,  setSaving]  = useState(false);
  const [msg,     setMsg]     = useState("");

  // ── 기본지역 / 주소 ──────────────────────────────────
  const [region,   setRegion]   = useState(null);   // {sido,sidoCd,sigungu,sigunguCd}
  const [emdList,  setEmdList]  = useState([]);     // 읍면동 목록
  const [emd,      setEmd]      = useState(null);   // 선택된 동 {code,bjdongCd,name}
  const [gungu,    setGungu]    = useState(null);   // 선택된 구 {gungu,sigunguCd,dongs}
  const [addrNo,   setAddrNo]   = useState("");     // 상세주소(번지) 키패드 입력
  // ── 주소 입력 방식 ───────────────────────────────────
  // "jibun" = 동 + 번지 (기존), "road" = 도로명 + 건물번호
  const [addrMode, setAddrMode] = useState("jibun");
  const [roads,    setRoads]    = useState([]);   // 도로명 목록 (본길 + 가지길)
  const [roadQ,    setRoadQ]    = useState("");   // 검색어 (초성 가능)
  const [roadBase, setRoadBase] = useState(null); // 선택한 본길 {name,subs}
  const [roadPick, setRoadPick] = useState("");   // 최종 도로명
  const [ho,       setHo]       = useState("");     // 호수
  const [hoAlt,    setHoAlt]    = useState("");     // 호수대체 (floor_etc) — "뒷쪽방" 등 자유 입력
  // 호수 입력 방식: "ho"(호수) / "alt"(호수대체) / "floor"(층대체)
  // 셋은 상세보기에서 순서대로 대체되는 값이라 하나만 채워야 한다.
  const [hoMode,   setHoMode]   = useState("ho");
  const [floorAlt, setFloorAlt] = useState("");     // 층대체   (floor_type2) — 옥탑/바닥층/주차층
  const [shopName, setShopName] = useState("");     // 상호명 (shop_name) — 상업용·업무용
  const [bldName,  setBldName]  = useState("");     // 건물명(상업용이면 상호명)

  // ── 건물정보 (백그라운드 조회 결과) ──────────────────
  const [bldInfo,     setBldInfo]     = useState(null);
  const [bldLoading,  setBldLoading]  = useState(false);
  const [bldError,    setBldError]    = useState("");

  // ── 물건 정보 ────────────────────────────────────────
  const [estateType, setEstateType] = useState("residence");
  const [bldType,    setBldType]    = useState("single-room");
  const [bldTypeEtc, setBldTypeEtc] = useState(false); // '기타' 드롭다운 표시 여부
  const [floor,      setFloor]      = useState("");
  const [roomCnt,    setRoomCnt]    = useState("1");
  const [bathCnt,    setBathCnt]    = useState("1");
  const [usedScale,  setUsedScale]  = useState("");    // 실평수

  const [structure, setStructure] = useState("");   // 룸형태 (bld_structure)
  const [shortDep,  setShortDep]  = useState("");   // 단기 예치금 (price_deposit)
  const [shortMon,  setShortMon]  = useState("");   // 단기 월세 (sale_type_rent_month)

  // ── 거래 조건 ────────────────────────────────────────
  const [deals,    setDeals]    = useState({ rent_type_2month: "y" }); // 기본 월세
  const [deposit,  setDeposit]  = useState("");  // 보증금 (fee_year)
  const [monthly,  setMonthly]  = useState("");  // 월세   (fee_month)
  const [saleP,    setSaleP]    = useState("");  // 매매가 (price_sale)
  const [jeonse,   setJeonse]   = useState("");  // 전세금 (fee_jeonse)
  const [mgrFee,   setMgrFee]   = useState("");  // 관리비 (price_per_mgr)
  const [premium,  setPremium]  = useState("");  // 권리금 (price_premium) — 상업용·업무용
  const [mgrItems, setMgrItems] = useState({});
  const [parking,  setParking]  = useState(false); // 기본: 주차불가

  // ── 연락처 / 메모 ────────────────────────────────────
  //   owners  = 임대인(집주인·관리인·가족·대리인)
  //   tenants = 임차인(세입자 등)
  //   각각 배열이며 meta API 가 JSON 으로 그대로 저장한다.
  //   관계 드롭다운 대신 이름칸 기본값으로 구분한다 (입력 단계 줄이기)
  const [owners,  setOwners]  = useState([{ name: "소유자", number: "" }]);
  const [tenants, setTenants] = useState([]);
  const [memo,    setMemo]    = useState("");

  // ── 사진 ─────────────────────────────────────────────
  const [photos, setPhotos] = useState([]);  // File 객체 배열

  // ── 녹음 ─────────────────────────────────────────────
  const [recording,  setRecording]  = useState(false);
  const [recSec,     setRecSec]     = useState(0);
  const recorderRef = useRef(null);   // MediaRecorder 인스턴스
  const chunksRef   = useRef([]);     // 녹음 데이터 조각
  const streamRef   = useRef(null);   // 마이크 스트림 (정지 시 해제용)
  const timerRef    = useRef(null);   // 경과시간 타이머

  // ── 키패드 표시 제어 ─────────────────────────────────
  // 한 번에 하나만 열리도록. "" 이면 모두 닫힘.
  const [pad, setPad] = useState("");

  // 키패드가 열리면 그 위치로 부드럽게 스크롤한다.
  //   폰에서는 키패드가 화면 아래에 가려 손으로 끌어올려야 하는 불편이 있다.
  useEffect(() => {
    if (!pad) return;
    const t = setTimeout(() => {
      document.querySelector(".np")?.scrollIntoView({
        behavior: "smooth",
        block: "center",   // 화면 한가운데로 오게
      });
    }, 100);   // 키패드가 그려진 뒤 움직이도록 잠깐 기다린다
    return () => clearTimeout(t);
  }, [pad]);

  // ========================================================
  // 녹음 시작 — [등록 시작] 버튼에서 호출
  //   권한이 거부되어도 폼은 정상 진행된다 (녹음만 없음)
  // ========================================================
  const startRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      chunksRef.current = [];

      // 브라우저마다 지원 형식이 달라 순서대로 시도한다
      const types = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4"];
      const mime = types.find((t) => MediaRecorder.isTypeSupported(t)) || "";

      const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      rec.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
      };
      rec.start(1000); // 1초마다 조각 저장 → 중간에 끊겨도 앞부분은 남는다
      recorderRef.current = rec;
      setRecording(true);

      // 경과 시간 표시
      setRecSec(0);
      timerRef.current = setInterval(() => setRecSec((s) => s + 1), 1000);
    } catch (e) {
      // 권한 거부·마이크 없음 — 폼은 그대로 진행
      console.warn("[녹음 시작 실패]", e.message);
      setMsg("녹음 권한이 없어 녹음 없이 진행합니다.");
      setRecording(false);
    }
  }, []);

  // ========================================================
  // 녹음 정지 → Blob 반환 (저장 시 호출)
  // ========================================================
  const stopRecording = useCallback(() => {
    return new Promise((resolve) => {
      const rec = recorderRef.current;
      if (timerRef.current) clearInterval(timerRef.current);
      if (!rec || rec.state === "inactive") return resolve(null);

      rec.onstop = () => {
        const blob = new Blob(chunksRef.current, {
          type: rec.mimeType || "audio/webm",
        });
        // 마이크 점유 해제 (안 하면 상단에 녹음중 표시가 계속 남는다)
        streamRef.current?.getTracks().forEach((t) => t.stop());
        setRecording(false);
        resolve(blob);
      };
      rec.stop();
    });
  }, []);

  // 화면을 떠날 때 마이크를 반드시 놓아준다
  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      streamRef.current?.getTracks().forEach((t) => t.stop());
    };
  }, []);

  // ========================================================
  // [등록 시작] — 권한 요청 + 기본지역 로드
  // ========================================================
  const handleStart = async () => {
    setStarted(true);
    startRecording();               // 권한 팝업 (거부해도 계속 진행)
    await loadRegion();
  };

  // 기본지역 읽기
  //   설정 구조: { sido, sidoCd, regions:[{ gungu, sigunguCd, dongs:[...] }] }
  //   구가 하나뿐이면 자동 선택해서 화면을 단순하게 유지한다.
  const loadRegion = async () => {
    try {
      const r = await authFetch("/api/staff-intake/region");
      const j = await r.json();
      const reg = j.region;
      setRegion(reg);

      // 관리탭에 등록된 첫 번째 구·동을 기본값으로 미리 골라둔다.
      //   현장에서 대부분 같은 지역을 다루므로 탭 두 번을 아낄 수 있다.
      //   다른 구·동이면 버튼을 눌러 바꾸면 된다.
      const list = Array.isArray(reg?.regions) ? reg.regions : [];
      if (list.length > 0) {
        const g = list[0];
        setGungu(g);
        loadRoads(g.gungu);
        // 첫 번째 법정동도 미리 선택
        if (Array.isArray(g.dongs) && g.dongs.length > 0) {
          setEmd({ code: g.dongs[0], name: g.dongs[0] });
        }
      }
    } catch (e) {
      setMsg("기본지역을 불러오지 못했습니다: " + e.message);
    }
  };

  // 구를 고르면 그 구의 도로명 목록을 새로 받아온다
  const pickGungu = (g) => {
    setGungu(g);
    // 구를 바꾸면 그 구의 첫 번째 동을 자동으로 고른다
    setEmd(
      Array.isArray(g.dongs) && g.dongs.length > 0
        ? { code: g.dongs[0], name: g.dongs[0] }
        : null
    );
    setRoadBase(null);  // 도로명 선택도 초기화
    setRoadPick("");
    setRoadQ("");
    loadRoads(g.gungu);
  };

  // 도로명 목록 — 등록된 매물에서 뽑아온다 (623개 수준, 한 번만 로드)
  const loadRoads = async (gungu) => {
    try {
      const r = await authFetch(
        `/api/staff-intake/roads?gungu=${encodeURIComponent(gungu)}`
      );
      const j = await r.json();
      setRoads(Array.isArray(j.roads) ? j.roads : []);
    } catch (e) {
      setRoads([]);
      console.warn("[도로명 목록 실패]", e.message);
    }
  };

  // ========================================================
  // 주소가 정해지면 백그라운드로 건물정보 조회
  //   직원은 기다리지 않고 다음 항목을 계속 입력한다.
  // ========================================================
  const fetchBuildingInfo = useCallback(async (jibunAddress) => {
    setBldLoading(true);
    setBldError("");
    try {
      const r = await authFetch(
        `/api/building-info?address=${encodeURIComponent(jibunAddress)}`
      );
      const j = await r.json();
      if (!j.ok) throw new Error(j.error || "조회 실패");
      setBldInfo(j.data);
    } catch (e) {
      setBldError(e.message);
      setBldInfo(null);
    } finally {
      setBldLoading(false);
    }
  }, []);

  // 동 + 번지가 모두 채워지면 자동으로 건물정보 조회 시작
  // 주소가 채워지면 백그라운드로 건물정보 조회
  //   지번 모드: 동 + 번지가 있어야 함
  //   도로명 모드: 도로명 + 건물번호가 있어야 함
  //   ※ 건물정보 API 는 지번주소만 받으므로, 도로명 모드일 때는
  //     /api/address/normalize 를 거쳐 지번으로 바꾼 뒤 조회한다.
  useEffect(() => {
    if (!region || !gungu) return;

    let query = "";
    if (addrMode === "jibun") {
      if (!emd || !addrNo) return;
      query = `${region.sido} ${gungu.gungu} ${emd.name} ${addrNo}`;
    } else {
      if (!roadPick || !addrNo) return;
      query = `${region.sido} ${gungu.gungu} ${roadPick} ${addrNo}`;
    }

    const t = setTimeout(async () => {
      if (addrMode === "jibun") {
        fetchBuildingInfo(query);
        return;
      }
      // 도로명 → 지번 변환 후 조회
      try {
        setBldLoading(true);
        const r = await authFetch(
          `/api/address/normalize?q=${encodeURIComponent(query)}`
        );
        const j = await r.json();
        const jibun = j?.items?.[0]?.jibunAddress || j?.items?.[0]?.address || "";
        if (jibun) fetchBuildingInfo(jibun);
        else { setBldError("도로명을 지번으로 바꾸지 못했습니다"); setBldLoading(false); }
      } catch (e) {
        setBldError(e.message);
        setBldLoading(false);
      }
    }, 800);   // 타이핑이 멈출 때까지 잠깐 기다린다

    return () => clearTimeout(t);
  }, [region, gungu, emd, addrNo, addrMode, roadPick, fetchBuildingInfo]);

  // ========================================================
  // 임시저장 — 값이 바뀔 때마다 브라우저에 보관
  // ========================================================
  useEffect(() => {
    if (!started) return;
    // 저장할 값 목록. 새 입력칸을 추가하면 여기에도 넣어야 백업된다.
    const draft = {
      emd, addrNo, ho, bldName, estateType, bldType, floor,
      roomCnt, bathCnt, usedScale, deals, deposit, monthly,
      saleP, jeonse, mgrFee, premium, mgrItems, parking,
      hoAlt, floorAlt, hoMode, shopName, structure, shortDep, shortMon, owners, tenants, memo,
    };
    try { localStorage.setItem(DRAFT_KEY, JSON.stringify(draft)); } catch (_) {}
  }, [started, emd, addrNo, ho, bldName, estateType, bldType, floor,
      roomCnt, bathCnt, usedScale, deals, deposit, monthly, saleP,
      jeonse, mgrFee, premium, mgrItems, parking, hoAlt, floorAlt,
      hoMode, shopName, structure, shortDep, shortMon, owners, tenants, memo]);

  // ── 토글 헬퍼 ────────────────────────────────────────
  const toggleDeal = (key) =>
    setDeals((p) => ({ ...p, [key]: p[key] === "y" ? "n" : "y" }));
  const toggleMgr = (key) =>
    setMgrItems((p) => ({ ...p, [key]: p[key] === "y" ? "n" : "y" }));

  // ── 호수로 해당층 추론 ───────────────────────────────
  //   EstateEditor.jsx 3219줄과 같은 규칙을 쓴다.
  //   4자리(1203)→12층, 3자리(302)→3층, 2자리 이하→1층
  //   B/지하 포함 시 지하층으로 처리.
  //   사용자가 층을 직접 고치면 그 값을 유지한다(자동값으로 덮지 않음).
  const inferFloor = (hoValue) => {
    const s = String(hoValue || "").trim().toUpperCase();
    const i = s.search(/[0-9]/);
    if (i === -1) return "";
    // [INTAKE_FIX_V1] 이어진 숫자만 센다 — 키패드 「호」 키로 "302호" 가 되면
    //   예전엔 4글자로 세어 「4자리→앞 2자리」 규칙에 걸려 30층이 됐음
    const digits = (s.substring(i).match(/^[0-9]+/) || [""])[0];
    // [INTAKE_FIX_V2] 키패드 「지」 키(지1호)도 지하 — 예전엔 "지하" 두 글자만 봐서 1층이 됐음
    const isBasement = s.includes("B") || s.includes("지");

    if (isBasement) {
      return digits.length >= 3
        ? `-${digits.substring(0, digits.length - 2)}`
        : "-1";
    }
    if (digits.length === 4) return digits.substring(0, 2);
    if (digits.length === 3) return digits.substring(0, 1);
    return "1";
  };

  // ── 연락처 목록 조작 ─────────────────────────────────
  //   setter 를 인자로 받아 owners/tenants 양쪽에 같은 함수를 쓴다.
  const addContact = (setter, defName) =>
    setter((p) => (p.length >= 5 ? p : [...p, { name: defName, number: "" }]));

  const removeContact = (setter, idx) =>
    setter((p) => p.filter((_, i) => i !== idx));

  // 전화번호 자동 하이픈
  //   숫자만 남긴 뒤 자릿수에 따라 나눈다.
  //   02(서울)는 지역번호가 2자리라 따로 처리한다.
  //   DB 에는 하이픈 포함으로 저장된다 (기존 contact_phone 과 동일한 형식).
  const formatPhone = (v) => {
    const d = String(v || "").replace(/\D/g, "").slice(0, 11);
    if (d.startsWith("02")) {
      if (d.length <= 2) return d;
      if (d.length <= 5) return `${d.slice(0, 2)}-${d.slice(2)}`;
      if (d.length <= 9) return `${d.slice(0, 2)}-${d.slice(2, 5)}-${d.slice(5)}`;
      return `${d.slice(0, 2)}-${d.slice(2, 6)}-${d.slice(6, 10)}`;
    }
    if (d.length <= 3) return d;
    if (d.length <= 7) return `${d.slice(0, 3)}-${d.slice(3)}`;
    if (d.length <= 10) return `${d.slice(0, 3)}-${d.slice(3, 6)}-${d.slice(6)}`;
    return `${d.slice(0, 3)}-${d.slice(3, 7)}-${d.slice(7)}`;
  };

  // 한 줄의 한 칸만 바꾼다 (이름/번호/관계)
  const updateContact = (setter, idx, field, val) =>
    setter((p) => p.map((c, i) => (i === idx ? { ...c, [field]: val } : c)));

  // ========================================================
  // 저장 — 기존 등록 API 4개를 순서대로 호출한다
  //   EstateEditor.jsx 3989줄의 신규등록 흐름과 동일한 순서.
  //   순서를 바꾸면 안 된다 (매물번호가 있어야 나머지가 저장 가능).
  // ========================================================
  const handleSave = async () => {
    // ── 필수값 검사 ──
    if (!gungu) return setMsg("지역(구)을 선택해 주세요.");
    if (addrMode === "jibun" && !emd)      return setMsg("동을 선택해 주세요.");
    if (addrMode === "road"  && !roadPick) return setMsg("도로명을 선택해 주세요.");
    if (!addrNo) return setMsg(addrMode === "jibun" ? "번지를 입력해 주세요." : "건물번호를 입력해 주세요.");

    // 준공일은 /api/properties/new 의 필수값이다.
    // 건물정보가 아직 안 왔으면 잠깐 기다려 본다.
    let completionYear = getCompletionYear();
    if (!completionYear && bldLoading) {
      setMsg("건물정보를 기다리는 중입니다...");
      for (let i = 0; i < 20 && bldLoading; i++) {
        await new Promise((r) => setTimeout(r, 500));  // 최대 10초 대기
      }
      completionYear = getCompletionYear();
    }
    if (!completionYear) {
      // 건물정보를 못 받았을 때 — 등록 자체가 막히면 안 되므로 오늘 날짜로 넣고
      // 메모에 표시해 나중에 사무실에서 고칠 수 있게 한다.
      const t = new Date();
      completionYear = `${t.getFullYear()}-01-01`;
      setMsg("건물정보를 못 받아 준공일을 임시값으로 저장합니다. 사무실에서 수정하세요.");
    }

    setSaving(true);
    try {
      // 입력 방식에 따라 주소 문자열을 만든 뒤,
      // 카카오 주소검색(/api/address/normalize)으로 지번·도로명을 모두 확보한다.
      //   → PC 에디터처럼 두 주소가 함께 저장되어 검색·표시 어디서든 쓸 수 있다.
      const isJibun = addrMode === "jibun";
      const typed = isJibun
        ? `${region.sido} ${gungu.gungu} ${emd.name} ${addrNo}`
        : `${region.sido} ${gungu.gungu} ${roadPick} ${addrNo}`;

      let jibunAddress = isJibun ? typed : "";
      let roadAddress  = isJibun ? "" : typed;

      let lat = null, lng = null;
      try {
        const nr = await authFetch(
          `/api/address/normalize?q=${encodeURIComponent(typed)}`
        );
        const nj = await nr.json();
        const item = nj?.items?.[0];
        if (item) {
          // 변환에 성공하면 둘 다 채운다. 실패해도 입력값은 그대로 저장된다.
          if (item.jibunAddress) jibunAddress = item.jibunAddress;
          if (item.roadAddress)  roadAddress  = item.roadAddress;
        }
      } catch (e) {
        console.warn("[주소 변환 실패]", e.message);
      }

      // 좌표 조회 — 없으면 지도에 마커가 찍히지 않는다
      try {
        const gr = await authFetch(
          `/api/staff-intake/geocode?q=${encodeURIComponent(jibunAddress || typed)}`
        );
        const gj = await gr.json();
        if (gj.ok) { lat = gj.lat; lng = gj.lng; }
      } catch (e) {
        console.warn("[좌표 조회 실패]", e.message);
      }
      const recap = bldInfo?.recapTitle || {};
      const dong0 = bldInfo?.dongList?.[0] || {};

      // ── 1) 매물 껍데기 생성 → estate_no 발급 ──
      const resNew = await authFetch("/api/properties/new", {
        method: "POST",
        body: JSON.stringify({
          estate_type: estateType,        // 필수
          completion_year: completionYear, // 필수
          jibunAddress,
          roadAddress,
          lat, lng,          // /new API 가 map_x/map_y 까지 함께 채운다
          detailAddress: ho ? `${ho}호` : "",
          register_memo: memo,
        }),
      });
      const newData = await resNew.json();
      if (!resNew.ok || !newData.ok) throw new Error(newData.error || "매물 생성 실패");
      const estateNo = newData.estate_no;

      // ── 2) 기본정보 ──
      await authFetch(`/api/properties/${estateNo}/basic`, {
        method: "PUT",
        body: JSON.stringify({
          estate_name: bldName,
          shop_name: shopName,       // 상호명 (상업용·업무용)
          estate_ho: ho,
          // 호수·호수대체·층대체는 셋 중 하나만 채운다.
          //   층대체는 코드값(parking)이 화면에 그대로 노출되는 문제가 있어
          //   한글 라벨("주차층")로 바꿔 호수대체 칸에 넣는다.
          floor_etc: hoMode === "floor"
            ? (FLOOR_ALT.find((f) => f.value === floorAlt)?.label || "")
            : hoAlt,
          floor_type2: hoMode === "floor" ? floorAlt : "",
          estate_type: estateType,
          bld_type: bldType,
          bld_structure: structure,   // 룸형태 (오픈형/분리형/복층형)
          floor: floor,
        }),
      });

      // ── 3) 상세정보 (금액·면적·방수) ──
      await authFetch(`/api/properties/${estateNo}/detail`, {
        method: "PUT",
        body: JSON.stringify({
          // 상업용·업무용은 방 개수를 세지 않으므로 보내지 않는다
          room_cnt: NEEDS_ROOM.includes(estateType) ? roomCnt : "",
          bath_cnt: NEEDS_ROOM.includes(estateType) ? bathCnt : "",
          living_condition_parking: parking ? "y" : "n",
          used_scale_square: usedScale,             // 실평수(평)
          completion_year: completionYear,
          // 거래유형 (선택 안 한 것은 'n' 으로 명시)
          ...DEAL_TYPES.reduce((acc, d) => {
            acc[d.key] = deals[d.key] === "y" ? "y" : "n";
            return acc;
          }, {}),
          // 금액
          fee_year: deposit,      // 보증금
          fee_month: monthly,     // 월세
          fee_jeonse: jeonse,     // 전세
          price_sale: saleP,      // 매매가

          price_per_mgr: mgrFee,  // 관리비
          price_premium: premium,  // 권리금 (상업용·업무용)
          price_deposit: shortDep,           // 단기 예치금
          sale_type_rent_month: shortMon,    // 단기 월세
          price_mgr_yn: mgrFee ? "y" : "n",
          // 관리비 포함 항목
          ...MGR_ITEMS.reduce((acc, m) => {
            acc[m.key] = mgrItems[m.key] === "y" ? "y" : "n";
            return acc;
          }, {}),
          // 건물정보에서 받은 값 (있을 때만)
          totArea: dong0.totArea || "",
          bld_scale: dong0.grndFlrCnt || "",
          bld_scale_under: dong0.ugrndFlrCnt || "",
          living_condition_ev: dong0.elevCnt > 0 ? "y" : "n",
        }),
      });

      // ── 4) 연락처 ──
      //   owners/tenants 는 JSON 배열로 저장된다.
      //   동시에 contact_name1~5 도 채워서 기존 화면·SMS 기능과 호환시킨다.
      const validOwners  = owners.filter((c) => c.name || c.number);
      const validTenants = tenants.filter((c) => c.name || c.number);
      const allContacts  = [...validOwners, ...validTenants].slice(0, 5);

      if (allContacts.length > 0 || memo) {
        const contactCols = {};
        allContacts.forEach((c, i) => {
          contactCols[`contact_name${i + 1}`]  = c.name || c.relation || "";
          contactCols[`contact_phone${i + 1}`] = c.number || "";
        });

        await authFetch(`/api/properties/${estateNo}/meta`, {
          method: "PUT",
          body: JSON.stringify({
            owners:  validOwners,
            tenants: validTenants,
            ...contactCols,
            register_memo: memo,
          }),
        });
      }

      // ── 5) 사진 업로드 (실패해도 매물은 이미 등록됨) ──
      for (let i = 0; i < photos.length; i++) {
        try {
          const fd = new FormData();
          fd.append("file", photos[i]);
          // authFetch 는 Content-Type 을 json 으로 고정하므로 FormData 엔 쓸 수 없다.
          // 토큰만 직접 붙여서 기본 fetch 를 쓴다.
          await fetch(`/api/properties/${estateNo}/photos`, {
            method: "POST",
            headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
            body: fd,
          });
        } catch (e) {
          console.warn("[사진 업로드 실패]", i, e.message);
        }
      }

      // ── 6) 녹음 업로드 (실패해도 매물은 이미 등록됨) ──
      const blob = await stopRecording();
      if (blob && blob.size > 0) {
        try {
          const fd = new FormData();
          fd.append("file", blob, "recording.webm");
          fd.append("estate_no", String(estateNo));
          await fetch("/api/staff-intake/recording", {
            method: "POST",
            headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
            body: fd,
          });
        } catch (e) {
          console.warn("[녹음 업로드 실패]", e.message);
        }
      }

      // ── 7) [INTAKE_BLD_FE_V1] 같은 건물 공통칸 맞추기 (편집기 저장 때와 같은 처리) ──
      //   bcode 보충 → 대장 값으로 용도지역·층수·사용승인일 등 교정. 실패해도 등록은 이미 완료.
      //   대장을 새로 조회해야 하면 오래 걸릴 수 있어 최대 8초만 기다린다
      //   (넘어도 서버는 끝까지 처리한다).
      //   백엔드: backend/routes/buildingSync.js  POST /api/properties/:id/sync-building
      try {
        await Promise.race([
          authFetch(`/api/properties/${estateNo}/sync-building`, { method: "POST" }),
          new Promise((r) => setTimeout(r, 8000)),
        ]);
      } catch (e) {
        console.warn("[같은 건물 동기화 실패]", e.message);
      }

      // ── 완료 ──
      localStorage.removeItem(DRAFT_KEY);
      alert(`등록 완료\n매물번호: ${estateNo}`);
      // navigate 는 React 내부 이동이라 목록을 다시 불러오지 않는다.
      // 방금 등록한 매물이 보이도록 페이지를 새로 연다.
      window.location.href = "/";
    } catch (e) {
      setMsg("저장 실패: " + e.message);
      setSaving(false);
    }
  };

  // 건물정보 응답에서 준공일 꺼내기
  //   서버가 주는 useAprDay("20181012") → "2018-10-12"
  //   BuildingInfoOverlay.jsx 395줄과 같은 규칙
  function getCompletionYear() {
    const raw = bldInfo?.dongList?.[0]?.useAprDay || "";
    if (String(raw).length !== 8) return "";
    return `${raw.slice(0, 4)}-${raw.slice(4, 6)}-${raw.slice(6, 8)}`;
  }

  // 사진 선택
  const onPickPhotos = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length) setPhotos((p) => [...p, ...files]);
    e.target.value = "";   // 같은 파일을 다시 고를 수 있게 초기화
  };

  // ========================================================
  // 화면
  // ========================================================
  if (!started) {
    return (
      <div className="si-wrap">
        <div className="si-start">
          <h2>물건 접수</h2>
          <p className="si-start-desc">
            시작하면 녹음이 함께 시작됩니다.<br />
            마이크 권한을 허용해 주세요.
          </p>
          <button className="si-start-btn" onClick={handleStart}>
            등록 시작
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="si-wrap">
      {recording && (
        <div className="si-rec">
          <span>
            🔴 녹음 중 {String(Math.floor(recSec / 60)).padStart(2, "0")}:
            {String(recSec % 60).padStart(2, "0")}
          </span>
          {/* 중지하면 그때까지의 녹음이 저장되고 다시 시작할 수 없다.
              저장 시점에도 자동으로 중지되므로 보통은 누를 필요가 없다. */}
          <button
            className="si-recstop"
            onClick={async () => {
              await stopRecording();
              setMsg("녹음을 중지했습니다.");
            }}
          >
            ■ 중지
          </button>
        </div>
      )}
      {msg && <div className="si-msg">{msg}</div>}

      {/* ── 1. 주소 ── */}
      <div className="si-sec">
        <div className="si-sec-title">
          주소 {region ? `· ${region.sido}` : "· 불러오는 중"}
          {gungu ? ` ${gungu.gungu}` : ""}
        </div>

        {/* 구가 2개 이상일 때만 선택 버튼을 보여준다 */}
        {region?.regions?.length > 1 && (
          <div className="si-chips" style={{ marginBottom: 8 }}>
            {region.regions.map((g) => (
              <button
                key={g.sigunguCd}
                className={"si-chip" + (gungu?.sigunguCd === g.sigunguCd ? " on" : "")}
                onClick={() => pickGungu(g)}
              >
                {g.gungu}
              </button>
            ))}
          </div>
        )}

        {/* 지번 / 도로명 전환 */}
        <div className="si-nums" style={{ marginBottom: 8 }}>
          <button
            className={"si-chip" + (addrMode === "jibun" ? " on" : "")}
            onClick={() => setAddrMode("jibun")}
          >동·번지</button>
          <button
            className={"si-chip" + (addrMode === "road" ? " on" : "")}
            onClick={() => setAddrMode("road")}
          >도로명</button>
        </div>

        {addrMode === "jibun" ? (
          /* ── 지번 방식: 설정에 등록된 법정동 버튼 ── */
          <div className="si-chips">
            {(gungu?.dongs || []).map((d) => (
              <button
                key={d}
                className={"si-chip" + (emd?.name === d ? " on" : "")}
                onClick={() => setEmd({ code: d, name: d })}
              >
                {d}
              </button>
            ))}
            {!gungu && <span style={{ color: "#999", fontSize: 13 }}>지역을 불러오는 중...</span>}
          </div>
        ) : (
          /* ── 도로명 방식: 초성 검색 → 본길 → 가지길 ── */
          <div>
            <input
              className="si-input"
              value={roadQ}
              onChange={(e) => { setRoadQ(e.target.value); setRoadBase(null); setRoadPick(""); }}
              placeholder="도로명 초성 또는 이름 (예: ㄴㅂㅅㅎㄹ)"
            />

            {/* 검색어가 있고 아직 본길을 안 골랐을 때 후보 표시 */}
            {roadQ && !roadBase && (
              <div className="si-chips" style={{ marginTop: 8 }}>
                {roads
                  .filter((r) => matchRoad(r.name, roadQ))
                  .slice(0, 12)
                  .map((r) => (
                    <button
                      key={r.name}
                      className="si-chip"
                      onClick={() => {
                        setRoadBase(r);
                        setRoadPick(r.name);   // 본길 자체도 주소가 될 수 있다
                        setRoadQ(r.name);
                      }}
                    >
                      {r.name}
                      {r.subs.length > 0 && (
                        <span style={{ fontSize: 11, opacity: .7 }}> +{r.subs.length}</span>
                      )}
                    </button>
                  ))}
              </div>
            )}

            {/* 본길을 골랐으면 가지길 표시 */}
            {roadBase && (
              <div className="si-chips" style={{ marginTop: 8 }}>
                <button
                  className={"si-chip" + (roadPick === roadBase.name ? " on" : "")}
                  onClick={() => setRoadPick(roadBase.name)}
                >
                  {roadBase.name} (본길)
                </button>
                {roadBase.subs.map((s) => (
                  <button
                    key={s.name}
                    className={"si-chip" + (roadPick === s.name ? " on" : "")}
                    onClick={() => setRoadPick(s.name)}
                  >
                    {s.name.replace(roadBase.name, "")}
                  </button>
                ))}
                <button
                  className="si-chip"
                  onClick={() => { setRoadBase(null); setRoadPick(""); setRoadQ(""); }}
                >
                  ↺ 다시
                </button>
              </div>
            )}

            {roadPick && (
              <div className="si-bld ok" style={{ marginTop: 8 }}>
                선택: {region?.sido} {region?.sigungu} {roadPick}
              </div>
            )}
          </div>
        )}

        <div className="si-row" style={{ marginTop: 8 }}>
          <span className="si-label">{addrMode === "jibun" ? "번지" : "건물번호"}</span>
          <button
            className={"si-padbtn" + (addrNo ? "" : " empty") + (pad === "addr" ? " open" : "")}
            onClick={() => setPad(pad === "addr" ? "" : "addr")}
          >
            {addrNo || "눌러서 입력"}
          </button>
        </div>
        {pad === "addr" && (
          <NumPad mode="addr" value={addrNo} onChange={setAddrNo}
            onDone={() => setPad("")} />
        )}

        <div className="si-row">
          <span className="si-label">건물명</span>
          <input
            className="si-input"
            value={bldName}
            onChange={(e) => setBldName(e.target.value)}
            placeholder="선택 입력"
          />
        </div>

        {/* 호수 · 호수대체 · 층대체는 셋 중 하나만 쓴다.
            상세보기가 호수 → 호수대체 → 층대체 순으로 대체 표시하기 때문. */}
        <div className="si-nums" style={{ marginTop: 8, marginBottom: 6 }}>
          {[["ho", "호수"], ["alt", "호수대체"], ["floor", "층대체"]].map(([k, l]) => (
            <button
              key={k}
              className={"si-chip" + (hoMode === k ? " on" : "")}
              onClick={() => {
                setHoMode(k);
                // 방식을 바꾸면 나머지 값은 비운다 (둘 이상 저장되지 않게)
                if (k !== "ho")    setHo("");
                if (k !== "alt")   setHoAlt("");
                if (k !== "floor") setFloorAlt("");
              }}
            >{l}</button>
          ))}
        </div>

        {hoMode === "ho" && (
          <>
            <div className="si-row">
              <button
                className={"si-padbtn" + (ho ? "" : " empty") + (pad === "ho" ? " open" : "")}
                onClick={() => setPad(pad === "ho" ? "" : "ho")}
              >
                {ho ? `${ho}호` : "눌러서 입력"}
              </button>
            </div>
            {pad === "ho" && (
              <NumPad
                mode="ho"
                value={ho}
                onChange={(v) => /* [INTAKE_FIX_V2] 「호」 는 표시에서만 한 번 */ setHo(String(v || "").replace(/호/g, ""))}
                onDone={() => {
                  setPad("");
                  // 층을 아직 안 정했으면 호수 앞자리로 자동 설정한다.
                  // 이미 층을 입력했으면 그 값을 존중해 덮어쓰지 않는다.
                  if (!floor) {
                    const f = inferFloor(ho);
                    if (f) setFloor(f);
                  }
                }}
              />
            )}
          </>
        )}
        {hoMode === "alt" && (
          <div className="si-row">
            <input className="si-input" value={hoAlt} placeholder="예: 뒷쪽방, 옥탑방"
              onChange={(e) => setHoAlt(e.target.value)} />
          </div>
        )}

        {hoMode === "floor" && (
          <div className="si-chips">
            {FLOOR_ALT.map((f) => (
              <button key={f.value}
                className={"si-chip" + (floorAlt === f.value ? " on" : "")}
                onClick={() => setFloorAlt(floorAlt === f.value ? "" : f.value)}
              >{f.label}</button>
            ))}
          </div>
        )}

        {/* 상호명 — 상업용·업무용에서만 (DB 컬럼 shop_name) */}
        {(estateType === "commerce" || estateType === "business") && (
          <div className="si-row">
            <span className="si-label">상호명</span>
            <input
              className="si-input"
              value={shopName}
              onChange={(e) => setShopName(e.target.value)}
              placeholder="예: 모모커피"
            />
          </div>
        )}

        {/* 건물정보 조회 상태 */}
        {bldLoading && <div className="si-bld loading">건물정보 조회 중...</div>}
        {!bldLoading && bldInfo && (
          <div className="si-bld ok">
            ✓ {bldInfo.dongList?.[0]?.mainPurpsCdNm || "-"} ·
            준공 {getCompletionYear() || "-"} ·
            지상 {bldInfo.dongList?.[0]?.grndFlrCnt ?? "-"}층
          </div>
        )}
        {!bldLoading && bldError && (
          <div className="si-bld err">건물정보 조회 실패: {bldError}</div>
        )}
      </div>

      {/* ── 2. 물건유형 / 용도 ── */}
      <div className="si-sec">
        <div className="si-sec-title">물건유형</div>
        <div className="si-chips">
          {ESTATE_TYPES.map((t) => (
            <button
              key={t.value}
              className={"si-chip" + (estateType === t.value ? " on" : "")}
              onClick={() => {
                setEstateType(t.value);
                // 물건유형에 맞는 용도를 미리 골라둔다 (탭 한 번 절약)
                //   상업용 → 상가 / 업무용 → 사무실 / 주거·수익 → 원룸
                setBldTypeEtc(false);
                if (t.value === "commerce")      setBldType("shop");
                else if (t.value === "business") setBldType("office");
                else                             setBldType("single-room");
              }}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* 상호명 — 상업용·업무용에서만 (DB 컬럼 shop_name) */}
        {(estateType === "commerce" || estateType === "business") && (
          <div className="si-row" style={{ marginTop: 10 }}>
            <span className="si-label">상호명</span>
            <input
              className="si-input"
              value={shopName}
              onChange={(e) => setShopName(e.target.value)}
              placeholder="예: 모모커피"
            />
          </div>
        )}

        <div className="si-sec-title" style={{ marginTop: 12 }}>물건용도</div>
        <div className="si-chips">
          {BLD_QUICK.map((t) => (
            <button
              key={t.value}
              className={"si-chip" + (bldType === t.value && !bldTypeEtc ? " on" : "")}
              onClick={() => { setBldType(t.value); setBldTypeEtc(false); }}
            >
              {t.label}
            </button>
          ))}
          <button
            className={"si-chip" + (bldTypeEtc ? " on" : "")}
            onClick={() => setBldTypeEtc(true)}
          >
            기타
          </button>
        </div>
        {bldTypeEtc && (
          <select
            className="si-input"
            style={{ marginTop: 8 }}
            value={bldType}
            onChange={(e) => setBldType(e.target.value)}
          >
            <option value="">— 용도 선택 —</option>
            {BLD_ALL.map((t) => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>
        )}

        {/* 룸형태 — 주거용·수익성에서만 (DB 컬럼 bld_structure) */}
        {NEEDS_ROOM.includes(estateType) && (
          <>
            <div className="si-sec-title" style={{ marginTop: 12 }}>룸형태</div>
            <div className="si-chips">
              {ROOM_STRUCTURES.map((s) => (
                <button
                  key={s.value}
                  className={"si-chip" + (structure === s.value ? " on" : "")}
                  onClick={() => setStructure(structure === s.value ? "" : s.value)}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </>
        )}

      </div>

      {/* ── 3. 해당층 ── */}
      <div className="si-sec">
        <div className="si-sec-title">해당층</div>
        <div className="si-row">
          <button
            className={"si-padbtn" + (floor ? "" : " empty") + (pad === "floor" ? " open" : "")}
            onClick={() => setPad(pad === "floor" ? "" : "floor")}
          >
            {floor ? `${floor}층` : "눌러서 입력"}
          </button>
        </div>
        {pad === "floor" && (
          <NumPad mode="floor" value={floor} onChange={setFloor}
            onDone={() => setPad("")} />
        )}
      </div>

      {/* ── 4. 거래유형 · 금액 ── */}
      <div className="si-sec">
        <div className="si-sec-title">거래유형 (중복 선택 가능)</div>
        <div className="si-chips">
          {DEAL_TYPES.map((d) => (
            <button
              key={d.key}
              className={"si-chip" + (deals[d.key] === "y" ? " on" : "")}
              onClick={() => toggleDeal(d.key)}
            >
              {d.label}
            </button>
          ))}
        </div>

        {/* 선택한 거래유형에 해당하는 금액칸만 나타난다 */}
        {deals.rent_type_2month === "y" && (
          <>
            <div className="si-row" style={{ marginTop: 10 }}>
              <span className="si-label">보증금</span>
              <input className="si-input" inputMode="numeric" value={deposit}
                onChange={(e) => setDeposit(e.target.value)} placeholder="1000" />
              <span className="si-unit">만원</span>
            </div>
            <div className="si-row">
              <span className="si-label">월세</span>
              <input className="si-input" inputMode="numeric" value={monthly}
                onChange={(e) => setMonthly(e.target.value)} placeholder="50" />
              <span className="si-unit">만원</span>
            </div>
          </>
        )}
        {deals.rent_type_1year === "y" && (
          <div className="si-row" style={{ marginTop: 10 }}>
            <span className="si-label">전세보증금</span>
            <input className="si-input" inputMode="numeric" value={jeonse}
              onChange={(e) => setJeonse(e.target.value)} placeholder="15000" />
            <span className="si-unit">만원</span>
          </div>
        )}
        {deals.rent_type_4deal === "y" && (
          <div className="si-row" style={{ marginTop: 10 }}>
            <span className="si-label">매매가</span>
            <input className="si-input" inputMode="numeric" value={saleP}
              onChange={(e) => setSaleP(e.target.value)} placeholder="30000" />
            <span className="si-unit">만원</span>
          </div>
        )}
        {/* 단기임대 — 예치금 + 월세 (price_deposit / sale_type_rent_month) */}
        {deals.rent_type_3short === "y" && (
          <>
            <div className="si-row" style={{ marginTop: 10 }}>
              <span className="si-label">예치금</span>
              <input className="si-input" inputMode="numeric" value={shortDep}
                onChange={(e) => setShortDep(e.target.value)} placeholder="100" />
              <span className="si-unit">만원</span>
            </div>
            <div className="si-row">
              <span className="si-label">단기월세</span>
              <input className="si-input" inputMode="numeric" value={shortMon}
                onChange={(e) => setShortMon(e.target.value)} placeholder="80" />
              <span className="si-unit">만원</span>
            </div>
          </>
        )}

        {/* 권리금 — 상업용·업무용에서만 */}
        {(estateType === "commerce" || estateType === "business") && (
          <div className="si-row" style={{ marginTop: 10 }}>
            <span className="si-label">권리금</span>
            <input className="si-input" inputMode="numeric" value={premium}
              onChange={(e) => setPremium(e.target.value)} placeholder="3000" />
            <span className="si-unit">만원</span>
          </div>
        )}

        <div className="si-row" style={{ marginTop: 10 }}>
          <span className="si-label">관리비</span>
          <input className="si-input" inputMode="numeric" value={mgrFee}
            onChange={(e) => setMgrFee(e.target.value)} placeholder="5" />
          <span className="si-unit">만원</span>
        </div>
        <div className="si-sec-title" style={{ marginTop: 8 }}>관리비 포함 항목</div>
        <div className="si-chips">
          {MGR_ITEMS.map((m) => (
            <button
              key={m.key}
              className={"si-chip" + (mgrItems[m.key] === "y" ? " on" : "")}
              onClick={() => toggleMgr(m.key)}
            >
              {m.label}
            </button>
          ))}
        </div>
      </div>

      {/* ── 5. 방 · 욕실 · 주차 · 실평수 ── */}
      <div className="si-sec">
        {/* 상업용·업무용은 방 개수를 세지 않으므로 숨긴다 */}
        {NEEDS_ROOM.includes(estateType) && (
          <>
            <div className="si-sec-title">방 개수</div>
            <div className="si-nums">
              {["1", "2", "3", "4", "5"].map((n) => (
                <button key={n} className={"si-chip" + (roomCnt === n ? " on" : "")}
                  onClick={() => setRoomCnt(n)}>{n}</button>
              ))}
            </div>

            <div className="si-sec-title" style={{ marginTop: 12 }}>욕실 개수</div>
            <div className="si-nums">
              {["1", "2", "3"].map((n) => (
                <button key={n} className={"si-chip" + (bathCnt === n ? " on" : "")}
                  onClick={() => setBathCnt(n)}>{n}</button>
              ))}
            </div>
          </>
        )}

        <div className="si-sec-title" style={{ marginTop: 12 }}>주차</div>
        <button
          className={"si-chip" + (parking ? " on" : "")}
          style={{ width: "100%" }}
          onClick={() => setParking(!parking)}
        >
          {parking ? "주차 가능" : "주차 불가"}
        </button>

        <div className="si-sec-title" style={{ marginTop: 12 }}>실평수</div>
        <div className="si-row">
          <button
            className={"si-padbtn" + (usedScale ? "" : " empty") + (pad === "area" ? " open" : "")}
            onClick={() => setPad(pad === "area" ? "" : "area")}
          >
            {usedScale ? `${usedScale}평` : "눌러서 입력"}
          </button>
        </div>
        {pad === "area" && (
          <NumPad mode="area" value={usedScale} onChange={setUsedScale}
            onDone={() => setPad("")} />
        )}
      </div>

      {/* ── 6. 사진 ── */}
      <div className="si-sec">
        <div className="si-sec-title">사진 ({photos.length}장)</div>
        <div className="si-photos">
          {photos.map((f, i) => (
            <div className="si-photo" key={i}>
              <img src={URL.createObjectURL(f)} alt="" />
              <button className="si-photo-del"
                onClick={() => setPhotos((p) => p.filter((_, j) => j !== i))}>×</button>
            </div>
          ))}
          <label className="si-photo-add" style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
            +
            <input type="file" accept="image/*" capture="environment" multiple
              style={{ display: "none" }} onChange={onPickPhotos} />
          </label>
        </div>
      </div>

      {/* ── 7. 연락처 · 메모 ── */}
      <div className="si-sec">
        {/* 임대인 — owners 배열에 저장 */}
        <div className="si-sec-title">임대인</div>
        {owners.map((c, i) => (
          <div key={`o${i}`} className="si-contact">
            <input className="si-input si-cname" value={c.name} placeholder="소유자"
              onChange={(e) => updateContact(setOwners, i, "name", e.target.value)} />
            <input className="si-input" inputMode="tel" value={c.number} placeholder="010-0000-0000"
              onChange={(e) => updateContact(setOwners, i, "number", formatPhone(e.target.value))} />
            <button className="si-del" onClick={() => removeContact(setOwners, i)}>×</button>
          </div>
        ))}
        {owners.length < 5 && (
          <button className="si-add" onClick={() => addContact(setOwners, "소유자")}>
            + 임대인 추가
          </button>
        )}

        {/* 임차인 — tenants 배열에 저장 */}
        <div className="si-sec-title" style={{ marginTop: 14 }}>임차인</div>
        {tenants.map((c, i) => (
          <div key={`t${i}`} className="si-contact">
            <input className="si-input si-cname" value={c.name} placeholder="임차인"
              onChange={(e) => updateContact(setTenants, i, "name", e.target.value)} />
            <input className="si-input" inputMode="tel" value={c.number} placeholder="010-0000-0000"
              onChange={(e) => updateContact(setTenants, i, "number", formatPhone(e.target.value))} />
            <button className="si-del" onClick={() => removeContact(setTenants, i)}>×</button>
          </div>
        ))}
        {tenants.length < 5 && (
          <button className="si-add" onClick={() => addContact(setTenants, "임차인")}>
            + 임차인 추가
          </button>
        )}


        <div className="si-sec-title" style={{ marginTop: 12 }}>관리자 메모</div>
        <textarea className="si-textarea" value={memo}
          onChange={(e) => setMemo(e.target.value)}
          placeholder="현장 특이사항" />
      </div>

      {/* ── 저장 ── */}
      <div className="si-save">
        <button className="si-save-btn" disabled={saving} onClick={handleSave}>
          {saving ? "저장 중..." : "저장하기"}
        </button>
      </div>
    </div>
  );
}